import { useConnection } from "@solana/wallet-adapter-react";
import { useQuery } from "@tanstack/react-query";
import type { ReactNode, FC } from "react";
import {
  createContext,
  useContext,
  useCallback,
  useMemo,
  useState,
  useEffect,
} from "react";

import { useLocalStorageState } from "hooks/useLocalStorageState";
import {
  LAMPORTS,
  PrioritizationFeeLevels,
  getMaxPrioritizationFeeByPercentile,
  getMediumPrioritizationFeeByPercentile,
  getMinPrioritizationFeeByPercentile,
} from "services/getPrioritizationFeesByPercentile";

export const MAX_ACCEPTABLE_PRIORITY_FEE = 0.0003;
const MIN_LOW_PRIORITY_FEE_LAMPORTS = 10;
const MIN_MEDIUM_PRIORITY_FEE_LAMPORTS = 15;
const MIN_HIGH_PRIORITY_FEE_LAMPORTS = 20;

export const enum PrioritizationFeeType {
  LOW = "low",
  MEDIUM = "medium",
  HIGH = "high",
  CUSTOM = "custom",
}

interface PriorityFeeValues {
  low?: string;
  medium?: string;
  high?: string;
  custom: string;
}

interface PriorityFeeContextType {
  priorityFee: PriorityFeeValues;
  priorityFeeLamports: number;
  priorityFeeType: PrioritizationFeeType;
  isLoading: boolean;
  setCustomPriorityFee: (customFee: string) => void;
  getPriorityFeeForType: (type: PrioritizationFeeType) => string;
  setFeeType: (type: PrioritizationFeeType) => void;
}

const getPriorityFeeLamports = (priorityFee: number) =>
  Math.min(priorityFee, MAX_ACCEPTABLE_PRIORITY_FEE * LAMPORTS);
const getPriorityFeeStr = (priorityFeeLamports: number) =>
  (priorityFeeLamports / LAMPORTS).toFixed(9);

const defaultPriorityFeeValues: PriorityFeeValues = {
  low: undefined,
  medium: undefined,
  high: undefined,
  custom: getPriorityFeeStr(MIN_MEDIUM_PRIORITY_FEE_LAMPORTS),
};

export const PriorityFeeContext = createContext<PriorityFeeContextType>({
  priorityFee: defaultPriorityFeeValues,
  priorityFeeLamports: getPriorityFeeLamports(MIN_MEDIUM_PRIORITY_FEE_LAMPORTS),
  priorityFeeType: PrioritizationFeeType.MEDIUM,
  isLoading: false,
  setCustomPriorityFee: () => {},
  getPriorityFeeForType: () =>
    getPriorityFeeStr(MIN_MEDIUM_PRIORITY_FEE_LAMPORTS),
  setFeeType: () => {},
});

export const PriorityFeeProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { connection } = useConnection();

  const [customFeeLocalStorage, setCustomFeeLocalStorage] =
    useLocalStorageState("userTransactionPriorityFee", "");

  const [priorityFeeTypeLS, setPriorityFeeTypeLS] = useLocalStorageState(
    "userTransactionPriorityFeeType",
    ""
  );

  const [priorityFeeType, setPriorityFeeType] = useState(
    (priorityFeeTypeLS as PrioritizationFeeType) || PrioritizationFeeType.MEDIUM
  );
  const [customFee, setCustomFee] = useState(
    customFeeLocalStorage || getPriorityFeeStr(MIN_MEDIUM_PRIORITY_FEE_LAMPORTS)
  );

  const fetchFee = async (level: PrioritizationFeeLevels) => {
    switch (level) {
      case PrioritizationFeeLevels.LOW:
        return getMinPrioritizationFeeByPercentile(connection, {
          percentile: level,
          fallback: true,
        });
      case PrioritizationFeeLevels.MEDIUM:
        return getMediumPrioritizationFeeByPercentile(connection, {
          percentile: level,
          fallback: true,
        });
      case PrioritizationFeeLevels.HIGH:
        return getMaxPrioritizationFeeByPercentile(connection, {
          percentile: level,
          fallback: true,
        });
      default:
        return undefined;
    }
  };

  const { data: low, isLoading: isLowPriorityFeeLoading } = useQuery(
    ["priorityFeeLow"],
    () => fetchFee(PrioritizationFeeLevels.LOW),
    {
      refetchInterval: 30000,
    }
  );
  const { data: medium, isLoading: isMediumPriorityFeeLoading } = useQuery(
    ["priorityFeeMedium"],
    () => fetchFee(PrioritizationFeeLevels.MEDIUM),
    { refetchInterval: 30000 }
  );
  const { data: high, isLoading: isHighPriorityFeeLoading } = useQuery(
    ["priorityFeeHigh"],
    () => fetchFee(PrioritizationFeeLevels.HIGH),
    {
      refetchInterval: 30000,
    }
  );

  const getPriorityFeesLamports = useCallback(
    (type: PrioritizationFeeType) => {
      switch (type) {
        case PrioritizationFeeType.LOW:
          return getPriorityFeeLamports(
            Math.max(
              low || MIN_LOW_PRIORITY_FEE_LAMPORTS,
              MIN_LOW_PRIORITY_FEE_LAMPORTS
            )
          );
        case PrioritizationFeeType.MEDIUM:
          return getPriorityFeeLamports(
            Math.max(
              medium || MIN_MEDIUM_PRIORITY_FEE_LAMPORTS,
              MIN_MEDIUM_PRIORITY_FEE_LAMPORTS
            )
          );
        case PrioritizationFeeType.HIGH:
          return getPriorityFeeLamports(
            Math.max(
              high || MIN_HIGH_PRIORITY_FEE_LAMPORTS,
              MIN_HIGH_PRIORITY_FEE_LAMPORTS
            )
          );
        case PrioritizationFeeType.CUSTOM:
          return getPriorityFeeLamports(
            Math.round(parseFloat(customFee) * LAMPORTS)
          );
        default:
          return MIN_LOW_PRIORITY_FEE_LAMPORTS;
      }
    },
    [customFee, high, low, medium]
  );

  const getPriorityFeeForType = useCallback(
    (type: PrioritizationFeeType) => {
      switch (type) {
        case PrioritizationFeeType.LOW:
          return getPriorityFeeStr(
            getPriorityFeesLamports(PrioritizationFeeType.LOW)
          );
        case PrioritizationFeeType.MEDIUM:
          return getPriorityFeeStr(
            getPriorityFeesLamports(PrioritizationFeeType.MEDIUM)
          );
        case PrioritizationFeeType.HIGH:
          return getPriorityFeeStr(
            getPriorityFeesLamports(PrioritizationFeeType.HIGH)
          );
        case PrioritizationFeeType.CUSTOM:
          return customFee;
        default:
          return getPriorityFeeStr(MIN_LOW_PRIORITY_FEE_LAMPORTS);
      }
    },
    [getPriorityFeesLamports, customFee]
  );

  useEffect(() => {
    if (medium && !customFeeLocalStorage) {
      const updatedMedium = getPriorityFeeForType(PrioritizationFeeType.MEDIUM);
      setCustomFee(updatedMedium);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getPriorityFeeForType, medium]);

  useEffect(() => {
    if (priorityFeeType !== PrioritizationFeeType.CUSTOM) {
      const updatedFee = getPriorityFeeForType(priorityFeeType);
      setCustomFee(updatedFee);
    }
  }, [getPriorityFeeForType, priorityFeeType]);

  const setCustomPriorityFee = useCallback((customPriorityFee: string) => {
    setCustomFeeLocalStorage(customPriorityFee);
    setCustomFee(customPriorityFee);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setFeeType = useCallback((feeType: PrioritizationFeeType) => {
    setPriorityFeeTypeLS(feeType);
    setPriorityFeeType(feeType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const priorityFeeLamports = Math.round(parseFloat(customFee) * LAMPORTS);

  const value = useMemo(
    () => ({
      priorityFee: {
        low: getPriorityFeeForType(PrioritizationFeeType.LOW),
        medium: getPriorityFeeForType(PrioritizationFeeType.MEDIUM),
        high: getPriorityFeeForType(PrioritizationFeeType.HIGH),
        custom: customFee,
      },
      priorityFeeLamports,
      priorityFeeType,
      isLoading:
        isLowPriorityFeeLoading ||
        isMediumPriorityFeeLoading ||
        isHighPriorityFeeLoading,
      setCustomPriorityFee,
      getPriorityFeeForType,
      setFeeType,
    }),
    [
      getPriorityFeeForType,
      customFee,
      priorityFeeLamports,
      isLowPriorityFeeLoading,
      isMediumPriorityFeeLoading,
      isHighPriorityFeeLoading,
      priorityFeeType,
      setCustomPriorityFee,
      setFeeType,
    ]
  );

  return (
    <PriorityFeeContext.Provider value={value}>
      {children}
    </PriorityFeeContext.Provider>
  );
};

export const usePriorityFee = () => {
  const context = useContext(PriorityFeeContext);
  if (!context) {
    throw new Error("usePriorityFee must be used within a PriorityFeeProvider");
  }
  return context;
};
